import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../../util/reactIntl';
import { NamedLink } from '../../../../components';

import css from './CategoryMegaMenu.module.css';

/** Small chevron used on the trigger button */
const ChevronIcon = ({ open }) => (
  <svg
    className={classNames(css.chevron, { [css.chevronOpen]: open })}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2 4l4 4 4-4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Build the search-string that filters by one or two category levels.
 * The Sharetribe category filter key format is pub_categoryLevel1, pub_categoryLevel2, etc.
 *
 * @param {string} level1Id  top-level category id
 * @param {string} [level2Id] optional subcategory id
 * @returns {string}  URL query string, e.g. "?pub_categoryLevel1=dresses"
 */
const buildCategorySearch = (level1Id, level2Id) => {
  const params = new URLSearchParams({ pub_categoryLevel1: level1Id });
  if (level2Id) {
    params.set('pub_categoryLevel2', level2Id);
  }
  return `?${params.toString()}`;
};

/**
 * CategoryMegaMenu
 *
 * Renders a "Browse" trigger button in the desktop topbar. On hover (or focus), a full-width
 * mega-panel drops down showing all top-level categories from config.categoryConfiguration.categories
 * and their subcategories in a responsive column grid.
 *
 * @component
 * @param {Object}   props
 * @param {Array}    props.categories  — config.categoryConfiguration.categories
 * @returns {JSX.Element|null}
 */
const CategoryMegaMenu = ({ categories }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const closeTimerRef = useRef(null);
  const rootRef = useRef(null);

  const scheduleClose = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setActiveCategory(null);
    }, 180);
  }, []);

  const cancelClose = useCallback(() => {
    clearTimeout(closeTimerRef.current);
  }, []);

  const handleOpen = useCallback(() => {
    cancelClose();
    setOpen(true);
  }, [cancelClose]);

  // Close on Escape key
  useEffect(() => {
    const onKeyDown = e => {
      if (e.key === 'Escape' && open) {
        setOpen(false);
        setActiveCategory(null);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  // Close when clicking outside
  useEffect(() => {
    const onClickOutside = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setActiveCategory(null);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  // Cleanup timer on unmount
  useEffect(() => () => clearTimeout(closeTimerRef.current), []);

  if (!categories || categories.length === 0) {
    return null;
  }

  const browseLabel = intl.formatMessage({ id: 'CategoryMegaMenu.browse' });
  const allLabel = intl.formatMessage({ id: 'CategoryMegaMenu.allListings' });

  // The "active" category whose subcategories are highlighted on the right panel.
  // Default to the first category if none selected yet.
  const currentCategory =
    activeCategory ?? (categories.length > 0 ? categories[0] : null);

  return (
    <div
      ref={rootRef}
      className={css.root}
      onMouseEnter={handleOpen}
      onMouseLeave={scheduleClose}
    >
      {/* Trigger button */}
      <button
        className={classNames(css.trigger, { [css.triggerOpen]: open })}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={browseLabel}
        onFocus={handleOpen}
      >
        {browseLabel}
        <ChevronIcon open={open} />
      </button>

      {/* Mega panel */}
      <div
        className={classNames(css.panel, { [css.panelOpen]: open })}
        role="region"
        aria-label={browseLabel}
        onMouseEnter={cancelClose}
      >
        <div className={css.panelInner}>
          {/* Left rail: top-level categories */}
          <nav className={css.categoryRail} aria-label={browseLabel}>
            {categories.map(cat => {
              const isActive = currentCategory?.id === cat.id;
              return (
                <div
                  key={cat.id}
                  className={classNames(css.railItem, { [css.railItemActive]: isActive })}
                  onMouseEnter={() => {
                    cancelClose();
                    setActiveCategory(cat);
                  }}
                >
                  <NamedLink
                    name="SearchPage"
                    to={{ search: buildCategorySearch(cat.id) }}
                    className={css.railLink}
                    onClick={() => setOpen(false)}
                  >
                    {cat.name}
                  </NamedLink>
                  {cat.subcategories?.length > 0 && (
                    <svg
                      className={css.railChevron}
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 2l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Right panel: subcategories of active category */}
          {currentCategory && (
            <div className={css.subPanel}>
              {/* Active category heading */}
              <NamedLink
                name="SearchPage"
                to={{ search: buildCategorySearch(currentCategory.id) }}
                className={css.subPanelHeading}
                onClick={() => setOpen(false)}
              >
                {currentCategory.name}
                <span className={css.subPanelSeeAll}>
                  {intl.formatMessage({ id: 'CategoryMegaMenu.seeAll' })}
                </span>
              </NamedLink>

              {/* Subcategory grid */}
              {currentCategory.subcategories?.length > 0 ? (
                <div className={css.subGrid}>
                  {currentCategory.subcategories.map(sub => (
                    <NamedLink
                      key={sub.id}
                      name="SearchPage"
                      to={{ search: buildCategorySearch(currentCategory.id, sub.id) }}
                      className={css.subItem}
                      onClick={() => setOpen(false)}
                    >
                      {sub.name}
                      {/* Render third-level hints if they exist */}
                      {sub.subcategories?.length > 0 && (
                        <span className={css.subItemCount}>
                          {sub.subcategories.length}
                        </span>
                      )}
                    </NamedLink>
                  ))}
                </div>
              ) : (
                <p className={css.noSubcategories}>
                  {intl.formatMessage({ id: 'CategoryMegaMenu.noSubcategories' })}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer bar: browse all */}
        <div className={css.panelFooter}>
          <NamedLink name="SearchPage" className={css.allLink} onClick={() => setOpen(false)}>
            {allLabel}
          </NamedLink>
        </div>
      </div>
    </div>
  );
};

export default CategoryMegaMenu;
